import { useCallback } from 'react';
import { DropdownMenu, FormField } from 'taboola-ultimate-ui';
import { withIndication } from 'modules/errors/components';
import { useEntityType } from 'modules/push-api/hooks/useEntityType';
import { useFetchEntityTypes } from 'modules/push-api/hooks/useFetchEntityTypes';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useModal } from 'modules/taboola-common-frontend-modules/modals';
import { useValidationContext } from 'modules/taboola-common-frontend-modules/validations/ValidationContext';
import styles from './entityDropdown.module.scss';

const FormFieldWithIndication = withIndication(FormField);
const formFieldId = 'entity-selector';

const switchEntityMessages = {
    title: (
        <h2>
            <FormattedMessage id="app.modal.title.sure" defaultMessage="Are You Sure?" />
        </h2>
    ),
    formProps: {
        submitButtonText: <FormattedMessage id="app.actionButtons.yes.proceed" defaultMessage="Yes, Proceed" />,
    },
    content: (
        <FormattedMessage
            id="form.entity.reset.form"
            defaultMessage="Switching to another entity will clear the form. Are you sure you want to proceed?"
        />
    ),
};

export const EntityDropdown = () => {
    const { type, setTypeOnChange } = useEntityType();
    const { data: entityTypes, isLoading } = useFetchEntityTypes();
    const { resetFormToInitial } = useFormDataContext();
    const { reset: resetValidationContext } = useValidationContext();
    const { open: openModal } = useModal();

    const handleOnChange = useCallback(
        async entityType => {
            const result = await openModal(switchEntityMessages);

            if (result) {
                resetFormToInitial();
                resetValidationContext();
                setTypeOnChange(entityType);
            }
        },
        [setTypeOnChange, resetFormToInitial, resetValidationContext, openModal]
    );

    return (
        <FormFieldWithIndication
            inputId={formFieldId}
            containerClass={styles['entity-container']}
            label={<FormattedMessage id="push.api.create.settings.entity.label" defaultMessage="Entity" />}
            labelClass={styles['label']}
        >
            <div className={styles['entity']}>
                <span className={styles['entity-icon']}>
                    <FormattedMessage id="push.api.create.settings.entity.icon" defaultMessage="For" />
                </span>
                <DropdownMenu
                    id={formFieldId}
                    selectedValueObject={{
                        value: type,
                    }}
                    options={entityTypes}
                    onChange={handleOnChange}
                    searchable
                    placeholder={
                        <FormattedMessage
                            id="push.api.create.settings.entity.placeholder"
                            defaultMessage="Select Entity"
                        />
                    }
                    aria-label="entity-dropdown"
                    className={styles['dropdown-menu']}
                    isLoading={isLoading}
                />
            </div>
        </FormFieldWithIndication>
    );
};
